/**
 * The child parts to declare inside a definition: a TABLE, not a dropdown.
 *
 * Patrick's sixth observation. A multi-select could say WHICH definitions to put
 * inside this one and had nowhere to say how many of each, so `[4]` seekers were
 * not expressible at all - and a multiplicity is the entire reason to declare a
 * usage rather than point at a definition twice. A row here is a definition plus a
 * multiplicity, which is exactly the row the API takes.
 *
 * ONE component for two places, because the back end deliberately takes the same
 * rows in both: `child_parts` on `POST /api/parts` at creation, and `children` on
 * `POST /api/parts/{id}/children` afterwards. The wizard and the pane's
 * add-a-child dialog therefore differ in what they do with the rows and in
 * nothing else.
 *
 * The options are handed in, and they come from `api.parts({usable_as_type:
 * true})` - the server's own answer to "may this be the declared type of a usage",
 * which excludes abstract definitions. Nothing here decides what abstract means,
 * because getting that wrong offers a bare `part x : Sensor` that the API then
 * refuses with a 422.
 *
 * What it assembles and refuses lives in `parts-editing.ts`, pure and tested
 * directly: "a row with a multiplicity sends the object form and one without sends
 * a bare string" is a claim about a value rather than about a form.
 */

import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { MONOSPACE } from '../../theme'
import {
  CHILD_TABLE_LABEL,
  MULTIPLICITY_HELP,
  emptyChildDraft,
  multiplicityRefusal,
} from './parts-editing'
import type { ChildDraft } from './parts-editing'
import { partOption } from './part-wizard-model'
import type { PartChoice } from './part-wizard-model'
import ElementPicker from '../common/ElementPicker'

export const CHILDREN_TABLE_NOTE =
  'One row per part to declare inside this one: what it is, and how many. A usage ' +
  'of each is created, named after its definition. Generic kinds are absent — the ' +
  'server offers only what may legally be the type of a usage.'

export default function ChildPartsTable({
  options,
  rows,
  onChange,
  disabled = false,
  error = '',
}: {
  /** From `api.parts({usable_as_type: true})`, never filtered here. */
  options: PartChoice[]
  rows: ChildDraft[]
  onChange: (rows: ChildDraft[]) => void
  disabled?: boolean
  /** The server's complaint about `child_parts` or `children`, if it made one. */
  error?: string
}) {
  const edit = (key: string, patch: Partial<ChildDraft>) =>
    onChange(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))

  const add = () =>
    // Keyed on the clock rather than on the length: removing row 2 of three and
    // adding one would otherwise reuse a key React has just seen, and the new
    // row would inherit the removed row's input state.
    onChange([...rows, emptyChildDraft(`row-${Date.now()}-${rows.length}`)])

  const remove = (key: string) => onChange(rows.filter((row) => row.key !== key))

  return (
    <Stack spacing={1}>
      <Table size="small" aria-label={CHILD_TABLE_LABEL}>
        <TableHead>
          <TableRow>
            <TableCell>Part</TableCell>
            <TableCell sx={{ width: '9rem' }}>
              <Tooltip title={MULTIPLICITY_HELP} describeChild>
                <span>Multiplicity</span>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ width: '3rem' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const shape = multiplicityRefusal(row.multiplicity)
            return (
              <TableRow key={row.key}>
                <TableCell>
                  <ElementPicker
                    options={options.map(partOption)}
                    value={row.definition || null}
                    disabled={disabled}
                    onChange={(next) => edit(row.key, { definition: next })}
                    ariaLabel={`Part ${index + 1}`}
                    error={Boolean(error)}
                    fullWidth
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    value={row.multiplicity}
                    disabled={disabled}
                    onChange={(event) => edit(row.key, { multiplicity: event.target.value })}
                    placeholder="(none)"
                    size="small"
                    error={Boolean(shape)}
                    helperText={shape}
                    slotProps={{
                      input: { sx: { fontFamily: MONOSPACE } },
                      htmlInput: { 'aria-label': `Multiplicity ${index + 1}` },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label={`Remove part ${index + 1}`}
                    onClick={() => remove(row.key)}
                    disabled={disabled}
                    size="small"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={add}
          disabled={disabled}
          variant="outlined"
        >
          Add a part
        </Button>
      </Box>

      <Typography variant="caption" color={error ? 'error.main' : 'text.secondary'}>
        {error || CHILDREN_TABLE_NOTE}
      </Typography>
    </Stack>
  )
}
